import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Microscope, ShieldCheck, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-3626" data-mp="32q9b2z3rh0av">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-3627" data-mp="37k3nriky8f0g" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-3628" data-mp="1f1fipjqfv32v">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-3629" data-mp="1f1ywqpcyx7bm">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-3630" data-mp="39fzxjfs2w4ec">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-3631" data-mp="34gwq5oomlm37">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3632" data-mp="st1oe1jr6m20" /> : <Plus className="w-4 h-4" data-id="element-3633" data-mp="sucuw94l27wt" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3634" data-mp="xrbh85i09akg">
        <p className="text-gray-600 leading-relaxed" data-id="element-3635" data-mp="1lfup4jx57lc0">{answer}</p>
      </div>
    </div>;
}
export function RootCanalLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Когда нужно лечить каналы?',
    a: 'При воспалении нерва (пульпит) или инфекции в корне (периодонтит). Симптомы: сильная боль, чувствительность к горячему/холодному, отек десны, боль при накусывании или ночная боль.'
  }, {
    q: 'Больно ли лечить каналы?',
    a: 'Нет. Лечение проводится под эффективной местной анестезией, поэтому сама процедура безболезненна. После лечения возможен временный дискомфорт в течение нескольких дней.'
  }, {
    q: 'Сколько стоит лечение каналов в Лондоне?',
    a: 'Стоимость зависит от количества каналов в зубе и сложности случая. После осмотра и рентгена мы предоставляем точный план лечения и смету.'
  }, {
    q: 'Сколько визитов нужно?',
    a: 'Иногда лечение можно завершить за 1 визит. В более сложных случаях или при наличии инфекции может потребоваться 2 визита. Это определяется после диагностики.'
  }, {
    q: 'Нужна ли коронка после лечения каналов?',
    a: 'В большинстве случаев да, особенно для жевательных зубов. Зуб после удаления нерва становится хрупким, и коронка защищает его от раскола и продлевает срок службы.'
  }, {
    q: 'Можно ли спасти зуб, если сильно болит?',
    a: 'В большинстве случаев да. Современная эндодонтия позволяет спасти даже сильно разрушенные или инфицированные зубы. Чем раньше вы обратитесь, тем больше шансов сохранить зуб.'
  }, {
    q: 'Делаете ли вы срочные визиты при острой боли?',
    a: 'Да, мы понимаем, что зубную боль нельзя терпеть. Свяжитесь с нами как можно скорее — мы постараемся найти ближайшее доступное время.'
  }, {
    q: 'Где находится клиника?',
    a: '360–364 City Road, London EC1V 2PY, рядом с Angel Station (Islington).'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Root Canal Treatment',
    description: 'Endodontic treatment to save a tooth that is badly decayed or infected.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3636" data-mp="1473l7ks0fx88">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3637" data-mp="zad54xg7h1mt" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3638" data-mp="u9yy9iqcbxqd" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3639" data-mp="3f1hk5c5kk5rl" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-[#F8F5F0] overflow-hidden" data-id="element-3640" data-mp="3a4isd09doawk">
        <div className="absolute inset-0 bg-[#EFE8DE] -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3641" data-mp="224a9wei3jhi3" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3642" data-mp="2640xk9cbthie">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3643" data-mp="1s49rqvvyr7fh">
            <div className="space-y-6" data-id="element-3644" data-mp="1x330kb0b2a9b">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFE8DE] text-[#2F4A3F] border border-[#D8C4A8] text-sm font-medium" data-id="element-3645" data-mp="1x5z1873jfd1g">
                <span className="relative flex h-2 w-2" data-id="element-3646" data-mp="1n6qaro7vsgju">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-600 opacity-75" data-id="element-3647" data-mp="19ea3gb7y5wd4"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-700" data-id="element-3648" data-mp="1ecldaly8s6vc"></span>
                </span>
                Эндодонтия (лечение каналов)
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2B2D2F] leading-tight tracking-tight" data-id="element-3649" data-mp="14exhdsowgtbl">
                Лечение каналов в Лондоне –{' '}
                <span className="text-forest-700" data-id="element-3650" data-mp="kvhv6koc8mat">Dentico Clinic</span>
                <br data-id="element-3651" data-mp="gt4x8ifu1sol" />
                <span className="text-2xl sm:text-3xl font-medium text-stone-500 mt-2 block" data-id="element-3652" data-mp="gtwl1736um4b">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3653" data-mp="prqg6ep7v9pw">
                Безболезненное лечение корневых каналов под микроскопом. Спасаем
                зубы от удаления. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-3654" data-mp="1dical36asvhf">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3655" data-mp="18ihl94268dyh">
                  <Microscope className="w-4 h-4 text-[#2F4A3F]" data-id="element-3656" data-mp="1nh1wr858923h" />
                  Лечение под микроскопом
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3657" data-mp="1if2s7uxr3z4y">
                  <Zap className="w-4 h-4 text-yellow-500" data-id="element-3658" data-mp="83o5kpqvwege" />
                  Без боли
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-3659" data-mp="d2hee4v87ha8">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-3660" data-mp="i1lpj11pnp37" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3661" data-mp="3ghwr5duzo4z2">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3662" data-mp="3lj3wl69nss2k">
                  <Button variant="primary" size="lg" className="w-full gap-2" data-id="element-3663" data-mp="3v9yk0dyn5xpl">
                    <Calendar className="w-5 h-5" data-id="element-3664" data-mp="2xsglkc4g5mbs" />
                    Записаться на приём
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3665" data-mp="32q007d524149">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-3666" data-mp="1ftsepv5ivxri">
                    <MessageCircle className="w-5 h-5" data-id="element-3667" data-mp="1aupa2btgftk2" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3668" data-mp="10ze1yccjxkla">
              <div className="absolute inset-0 bg-[#D8C4A8] rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3669" data-mp="110gueopivv3b"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt="Лечение каналов - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3670" data-mp="s625rvj3r87j" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT IS ROOT CANAL */}
      <section className="py-16 bg-white" data-id="element-3671" data-mp="3lrcb0ltnge89">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3672" data-mp="3lqax89l91dmu">
          <div className="text-center mb-10" data-id="element-3673" data-mp="3gtkl1xckf26p">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3674" data-mp="3gqoly5oze7go">
              Что такое лечение каналов?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3675" data-mp="3qiudxff8govv">
              Лечение корневых каналов (эндодонтия) — это процедура,
              направленная на спасение зуба, который был сильно поврежден
              кариесом или инфекцией. Когда бактерии проникают внутрь зуба к
              нерву (пульпе), это вызывает воспаление и боль. Если не провести
              лечение вовремя, инфекция может распространиться на кость и
              привести к потере зуба.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3676" data-mp="r3gwlxum3ydv">
              Современная эндодонтия позволяет очистить каналы от инфекции,
              запломбировать их и восстановить зуб, сохраняя его
              функциональность на долгие годы. Это единственная альтернатива
              удалению зуба при глубоком поражении.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3677" data-mp="ko0agfvy65go">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3678" data-mp="kp1pswjzxe47">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3679" data-mp="pncuksvfoyfk">
            Как проходит лечение
          </h2>
          <div className="grid md:grid-cols-5 gap-4" data-id="element-3680" data-mp="1dco0spvjzl13">
            {[{
            step: '01',
            title: 'Диагностика',
            desc: 'Рентген-снимок для оценки состояния каналов.'
          }, {
            step: '02',
            title: 'Анестезия',
            desc: 'Полное обезболивание для комфортного лечения.'
          }, {
            step: '03',
            title: 'Очистка',
            desc: 'Удаление инфицированных тканей и обработка каналов.'
          }, {
            step: '04',
            title: 'Пломбирование',
            desc: 'Герметичное заполнение каналов специальным материалом.'
          }, {
            step: '05',
            title: 'Восстановление',
            desc: 'Установка пломбы или коронки для защиты зуба.'
          }].map((item, idx) => <Card key={idx} className="p-5 relative overflow-hidden h-full" data-id="element-3681" data-mp="1rzmpid3101c4" data-mpi={idx}>
                <div className="text-5xl font-bold text-[#EFE8DE] absolute -top-2 -right-2 select-none" data-id="element-3682" data-mp="1n7cr08nuhsy8">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3683" data-mp="1i6p49z6nfy9t">
                  <h3 className="text-lg font-bold text-[#2B2D2F] mb-2" data-id="element-3684" data-mp="1dagi41u1b4t5">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-3685" data-mp="197k6iv2ww8yp">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. MODERN APPROACH */}
      <section className="py-16 bg-white" data-id="element-3686" data-mp="3fvu864gvoull">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3687" data-mp="3kv6v0p7r21o6">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3688" data-mp="3ktm2t68wquth">
            <div data-id="element-3689" data-mp="3oy3k8yrsow13">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3690" data-mp="3oxjzlaarftjd">
                Современный подход к лечению
              </h2>
              <ul className="space-y-4" data-id="element-3691" data-mp="2x5uoxy7py9q3">
                {['Использование дентального микроскопа для точности', 'Цифровая рентген-диагностика (минимальное облучение)', 'Эффективная анестезия (лечение без боли)', 'Возможность лечения за один визит', 'Сохранение даже сложных зубов'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-3692" data-mp="2di51g36tevmo" data-mpi={idx}>
                    <CheckCircle2 className="w-6 h-6 text-forest-600 flex-shrink-0 mt-0.5" data-id="element-3693" data-mp="2dfsok4fwzbgh" />
                    <span className="text-gray-700 text-lg" data-id="element-3694" data-mp="2ieviotucvx9o">{item}</span>
                  </li>)}
              </ul>
              <div className="mt-8" data-id="element-3695" data-mp="jzejhefiummg">
                <Link to="/ru/contact" data-id="element-3696" data-mp="k0fvpm89xf5r">
                  <Button size="lg" className="gap-2 !opacity-100 !bg-[#2F4A3F] !text-white !border !border-[#2F4A3F] hover:!bg-[#3A5A4D]" data-id="element-3697" data-mp="jxbm29c1hr7p">
                    <Calendar className="w-5 h-5" data-id="element-3698" data-mp="sxzxcwa1tdgw" />
                    Записаться на консультацию
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3699" data-mp="12pe1xigux3gw">
              <img src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=1200&q=80" alt="Современное лечение каналов" className="absolute inset-0 w-full h-full object-cover" data-id="element-3700" data-mp="1govpxvmbwcp7" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-[#EFE8DE]" data-id="element-3701" data-mp="34aqdf3tfgfcb">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3702" data-mp="34c1mnj5n6fat">
          <div className="text-center mb-12" data-id="element-3703" data-mp="38bscnkncgrea">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3704" data-mp="38eoaza33ti39">
              Почему стоит лечить каналы у нас
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3705" data-mp="2zji03woz89tv">
            {[{
            title: 'Сохранение зуба',
            desc: 'Мы боремся за каждый зуб, чтобы избежать удаления и имплантации.'
          }, {
            title: 'Без боли',
            desc: 'Современные анестетики гарантируют комфорт во время процедуры.'
          }, {
            title: 'Русский язык',
            desc: 'Вы точно поймете диагноз и план лечения.'
          }, {
            title: 'Опытные врачи',
            desc: 'Специалисты по эндодонтии, работающие со сложными случаями.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3706" data-mp="cdje76g2vj2x" data-mpi={idx}>
                <div className="w-12 h-12 bg-[#F8F5F0] rounded-full flex items-center justify-center text-[#2F4A3F] mb-4 mx-auto border border-[#D8C4A8]" data-id="element-3707" data-mp="8d99dx40uypn">
                  <ShieldCheck className="w-6 h-6" data-id="element-3708" data-mp="3ul8wp7fm94th" />
                </div>
                <h3 className="text-lg font-bold text-[#2B2D2F] mb-2" data-id="element-3709" data-mp="3und8scno5a4u">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3710" data-mp="r4jv7zuw4lao">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3711" data-mp="mc8yt6zczhmv">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3712" data-mp="hf0dixiwyxhe">
          <div className="text-center mb-10" data-id="element-3713" data-mp="hegxxngqkl6j">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3714" data-mp="1eump8lid81bx">
              Частые вопросы о лечении каналов
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3715" data-mp="1a238jfodsq2w">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3716" data-mp="1hfud7n5z3omc" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3717" data-mp="1wd4mqqz22kff">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3718" data-mp="20cdi209quqd3">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3719" data-mp="25c867w24x1ug">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3720" data-mp="2a2ok1h9vaphc">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-3721" data-mp="2a3zziecodloa">
              <Button variant="outline" size="sm" data-id="element-3722" data-mp="2f2uf77fciu46">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/neotlozhnaya-stomatologiya-london" data-id="element-3723" data-mp="2k2yz77fgq6z5">
              <Button variant="outline" size="sm" data-id="element-3724" data-mp="2o2hki4b74k2i">
                Неотложная помощь
              </Button>
            </Link>
            <Link to="/ru/services/crowns" data-id="element-3725" data-mp="39cmril3yjut8">
              <Button variant="outline" size="sm" data-id="element-3726" data-mp="39fsf1vwxuomu">
                Коронки
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-3727" data-mp="3jbvhl2vpcpab">
              <Button variant="outline" size="sm" data-id="element-3728" data-mp="3jastt3tof2ym">
                Цены
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-forest-900" data-id="element-3729" data-mp="2qmolsbnmudsr">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3730" data-mp="2qldhmaq9z414">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3731" data-mp="2lpelu22majqo">
            Не терпите зубную боль
          </h2>
          <p className="text-xl text-ivory/80 mb-8 max-w-2xl mx-auto" data-id="element-3732" data-mp="30euejnlpkz90">
            Сохраните свой зуб сегодня. Запишитесь на консультацию.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3733" data-mp="27wf7w4ihgxl8">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3734" data-mp="22xvlk2zz9jsc">
              <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#F8F5F0] !text-[#2F4A3F] !border !border-[#F8F5F0] hover:!bg-[#EFE8DE]" data-id="element-3735" data-mp="ieidu94mu3dw">
                <Calendar className="w-5 h-5" data-id="element-3736" data-mp="9gzowtnvb4wq" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3737" data-mp="dedumbccaujz">
              <Button size="lg" className="w-full gap-2 !opacity-100 !bg-[#EFE8DE] !text-[#2F4A3F] !border !border-[#D8C4A8] hover:!bg-[#2F4A3F] hover:!text-white" data-id="element-3738" data-mp="164mh2fdws14d">
                <MessageCircle className="w-5 h-5" data-id="element-3739" data-mp="x6ty918zabc8" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}